/**
 * Queue Zustand Store.
 *
 * Manages state for the unified attention queue. Primary data source is
 * GET /v1/attention (operations service), which carries the full payload
 * inline in `AttentionItem.details`.
 *
 * Detail-panel rendering for tool-approval items is driven by the
 * `buildSyntheticDetail` helper, which converts an AttentionItem into a
 * card-compatible response using the type-aware renderer. The legacy
 * /v1/approvals/{id} endpoint no longer exists; this avoids a 404.
 *
 * Approve / reject / suppress actions go through the unified
 * POST /v1/attention/{id}/resolve endpoint.
 */

import { create } from 'zustand'
import {
  getAttentionItems,
  getAttentionCount,
  getAttentionHistory,
  assignAttentionItem,
  unassignAttentionItem,
  getAttentionAssignments,
  resolveAttentionItem,
} from './api'
import { buildSyntheticDetail } from './buildSyntheticDetail'
import { ApiClientError } from '@/lib/api'
import { ASSIGNMENT_ENABLED } from './featureFlags'
import { useResolutionSignal } from './resolutionSignal'
import { useSessionsStore } from '@/features/sessions/store'
import type {
  ApprovalItem,
  AttentionItem,
  AttentionType,
  QueueFiltersState,
  ApprovalDetailResponse,
  RejectFeedback,
  AttentionQueueTab,
  OperatorTag,
} from './types'
import { isToolApproval } from './types'

/**
 * Polling interval in milliseconds (10 minutes).
 *
 * Background polling is intentionally infrequent — the queue receives
 * real-time updates via SSE, and operators trigger explicit refreshes
 * via the toolbar button or by navigating into the Queue view (which
 * forces an immediate refetch on mount). This interval exists only as
 * a slow safety net to keep the sidebar badge fresh when the operator
 * is on other pages and to recover from missed SSE events.
 */
const POLL_INTERVAL_MS = 600_000

/**
 * Foreground polling interval in milliseconds (5 seconds).
 *
 * Runs ONLY while the operator is actively mounted on the Attention Queue
 * view (started/stopped by QueueView's mount effect). When an operator
 * rejects an agent tool-approval the agent often chains a second approval
 * a few seconds later; this fast loop surfaces it promptly. It uses the
 * lightweight hash/count check (`fetchAttentionCount`) so each tick only
 * pulls the full list when something has actually changed.
 */
const FOREGROUND_POLL_INTERVAL_MS = 5_000

/** Debounce delay for search input in milliseconds. */
export const SEARCH_DEBOUNCE_MS = 300

/** localStorage key for the operator tag. */
const OPERATOR_TAG_KEY = 'stream_operator_tag'

/** Read the persisted operator tag from localStorage. */
function loadOperatorTag(): OperatorTag | null {
  try {
    const tag = localStorage.getItem(OPERATOR_TAG_KEY)
    if (tag && ['MJT', 'BH', 'JW', 'TF', 'MAL'].includes(tag)) {
      return tag as OperatorTag
    }
  } catch {
    // localStorage unavailable
  }
  return null
}

/** Persist the operator tag to localStorage. */
function saveOperatorTag(tag: OperatorTag | null): void {
  try {
    if (tag) {
      localStorage.setItem(OPERATOR_TAG_KEY, tag)
    } else {
      localStorage.removeItem(OPERATOR_TAG_KEY)
    }
  } catch {
    // localStorage unavailable
  }
}

/**
 * Serialise reject feedback (quick-reason chips + freetext) into a single
 * string for the unified resolve endpoint, which only accepts a `feedback`
 * field.
 *
 * The backend doesn't yet support a structured `quick_reasons` array on
 * the resolve endpoint, so we prefix the freetext with the chosen
 * reasons. When the backend gains structured-feedback support, this
 * helper goes away and `quick_reasons` is sent as-is.
 */
function serializeRejectFeedback(
  feedback: import('./types').RejectFeedback | string | undefined,
): string {
  if (!feedback) return ''
  if (typeof feedback === 'string') return feedback
  const reasons = (feedback.quick_reasons ?? []).filter(Boolean)
  const note = feedback.feedback?.trim() ?? ''
  if (reasons.length === 0) return note
  const reasonsLine = `[${reasons.join(', ')}]`
  return note ? `${reasonsLine} ${note}` : reasonsLine
}

/**
 * Result of an optimistic resolve.
 *
 * `alreadyResolved` is true when the backend returned the Task 3145f
 * idempotent-success envelope (`status === "already_resolved"`),
 * meaning the attention had already been cleared before the request
 * landed. Callers use this to render a neutral info toast instead of
 * the regular success toast.
 */
export interface OptimisticResolveResult {
  alreadyResolved: boolean
}

/**
 * Backend error code (4065a §4.2) returned with HTTP 502 when the liveness
 * authority (agent session) is unreachable or errors. Per the synchronous
 * resolve contract the item is NOT transitioned — it stays `pending` — so the
 * operator must see an explicit, actionable failure rather than a silent
 * success ("phantom resolved"). `performOptimisticResolve` restores the item
 * to the queue and rethrows with `AGENT_UNREACHABLE_MESSAGE`.
 */
export const AGENT_UNREACHABLE_ERROR_CODE = 'agent_unreachable'

/**
 * HTTP status the resolve contract (4065a §4.2) defines for the
 * agent-unreachable failure: the liveness authority could not be reached and
 * the item was NOT transitioned (stays `pending`).
 */
export const AGENT_UNREACHABLE_HTTP_STATUS = 502

/**
 * Operator-facing message for the `agent_unreachable` failure. Worded so the
 * operator knows (a) the resolve did not take effect and (b) the item is still
 * actionable in the queue.
 */
export const AGENT_UNREACHABLE_MESSAGE =
  "Couldn't reach the agent — item kept in the queue, try again"

/**
 * True when an error is the canonical `agent_unreachable` failure (HTTP 502,
 * per 4065a §4.2).
 *
 * Detection matches on EITHER the structured error code OR the HTTP 502 status:
 *
 *   - Structured code (primary, post-migration — Task 4069d): the operations
 *     `AttentionItemHandler`
 *     (`services/operations/backend/internal/handlers/http/attention_item_handler.go`)
 *     now emits the canonical platform envelope
 *     `{ "error": { "type": "system_error", "code": "agent_unreachable", … } }`
 *     (`packages/go/errors` `WriteJSON`) on the resolve 502. The API gateway is
 *     a transparent reverse proxy
 *     (`services/api/backend/internal/proxy/reverse_proxy.go`) and forwards that
 *     body verbatim, and the Stream API client reads `errorData.error?.code`
 *     into `ApiClientError.code` (`lib/api/client.ts`), so `code` is now
 *     populated as `"agent_unreachable"` for these responses. This is the exact,
 *     intentional signal.
 *   - HTTP 502 status (belt-and-suspenders): the 502 status is always preserved
 *     by the client (`response.status`) and is retained as a fallback. It still
 *     catches cases that do not carry the structured code — most importantly the
 *     gateway's own `UPSTREAM_UNAVAILABLE` 502 (operations itself unreachable,
 *     so no enveloped body is produced), which is operationally indistinguishable
 *     from the agent-unreachable case from the operator's point of view. Every
 *     502 on the resolve path means the same thing: the resolve did not commit
 *     and the (optimistically removed) item has been restored to the queue.
 */
export function isAgentUnreachableError(err: unknown): boolean {
  if (!(err instanceof ApiClientError)) {
    return false
  }
  return (
    err.statusCode === AGENT_UNREACHABLE_HTTP_STATUS ||
    err.code === AGENT_UNREACHABLE_ERROR_CODE
  )
}

/**
 * Deduplicate history items by ID, keeping the first occurrence (which will
 * be the most recent when optimistic prepends are involved).
 */
function deduplicateHistoryItems(items: AttentionItem[]): AttentionItem[] {
  const seen = new Set<string>()
  return items.filter((item) => {
    if (seen.has(item.id)) return false
    seen.add(item.id)
    return true
  })
}

/**
 * Optimistically resolve an attention item.
 *
 * Closes the expanded detail panel, removes the item from
 * `attentionItems`, and decrements `attentionCount` BEFORE the network
 * call so the UI updates instantly. Then POSTs the resolution. On
 * success: fires a background `refetch()` for eventual consistency
 * (NOT awaited so the caller's `await` returns immediately and the
 * action bar / modal can close without a "Rejecting..." spinner). On
 * failure: restores the item to its original index and rethrows so the
 * caller can show an error toast.
 *
 * This is the shared core of approve / reject / suppress /
 * approveWithEdits — they all need the same optimistic behaviour.
 *
 * Returns an OptimisticResolveResult so callers can branch on the
 * Task 3145f "already_resolved" idempotent-success envelope.
 */
async function performOptimisticResolve(
  get: () => QueueState & QueueActions,
  set: (
    partial:
      | Partial<QueueState & QueueActions>
      | ((
          state: QueueState & QueueActions,
        ) => Partial<QueueState & QueueActions>),
  ) => void,
  itemId: string,
  resolution: 'approve' | 'reject' | 'suppress',
  reviewerId: string,
  feedback?: string | null,
): Promise<OptimisticResolveResult> {
  const state = get()
  const index = state.attentionItems.findIndex((i) => i.id === itemId)
  // Look in history too — some callers (e.g. detail panel for a recently
  // resolved item) operate on the history list. If not present in either
  // we can't proceed.
  const item =
    index >= 0
      ? state.attentionItems[index]
      : state.historyItems.find((i) => i.id === itemId)

  if (!item) {
    throw new Error(
      `Cannot resolve item ${itemId}: not found in current list. Try refreshing the queue.`,
    )
  }

  // Snapshot for rollback. Only the live-list mutation is reversible —
  // we don't touch history.
  const liveSnapshot = index >= 0
    ? {
        index,
        item,
        prevCount: state.attentionCount,
        prevExpandedId: state.expandedItemId,
        prevDetailData: state.detailData,
      }
    : null

  // Optimistic UI updates: remove from list, decrement count, close
  // detail panel if it was showing this item.
  if (liveSnapshot) {
    set((s) => ({
      attentionItems: s.attentionItems.filter((i) => i.id !== itemId),
      attentionCount: Math.max(0, s.attentionCount - 1),
      expandedItemId: s.expandedItemId === itemId ? null : s.expandedItemId,
      detailData: s.expandedItemId === itemId ? null : s.detailData,
    }))
  }

  // Register the resolved id BEFORE the POST so the guard is active for
  // the entire round-trip — including the ~36 s Python agent call that
  // blocks tool-approval items.  Without this, any GET /v1/attention that
  // lands while the POST is in-flight (fire-and-forget refetches from
  // sibling resolves, route remounts, the refresh button) sees the server's
  // still-pending row and re-adds the item.  On POST failure the catch
  // block below removes the id so the item is visible on the next GET.
  markRecentlyResolved(set, [itemId])

  let alreadyResolved = false
  try {
    const resp = await resolveAttentionItem({
      id: item.id,
      source: item.source,
      attentionType: item.attention_type,
      resolution,
      executionId: item.execution_id,
      agentId: item.agent_id,
      sessionId: item.session_id,
      reviewerId,
      feedback: feedback ?? null,
      detailsSnapshot: item.details ?? undefined,
    })
    // Task 3145f: backend returns 200 with status="already_resolved"
    // when the attention had already been cleared before this request
    // landed (double-click, racing operators, agent self-completing).
    // Propagate so the caller can render a neutral info toast.
    alreadyResolved = resp?.status === 'already_resolved'
  } catch (e) {
    // Rollback the optimistic update so the operator can see the item
    // is still pending and retry.
    if (liveSnapshot) {
      set((s) => {
        // Only restore if the item isn't already back (e.g. via a
        // concurrent SSE push). Guard against duplicates.
        if (s.attentionItems.some((i) => i.id === itemId)) {
          return {} as Partial<QueueState & QueueActions>
        }
        const nextItems = [...s.attentionItems]
        // Clamp insertion index in case the list shrank concurrently.
        const insertAt = Math.min(liveSnapshot.index, nextItems.length)
        nextItems.splice(insertAt, 0, liveSnapshot.item)
        return {
          attentionItems: nextItems,
          attentionCount: liveSnapshot.prevCount,
          expandedItemId: liveSnapshot.prevExpandedId,
          detailData: liveSnapshot.prevDetailData,
        }
      })
    }
    // Remove from the recently-resolved guard: the POST failed so the
    // item is still pending and must remain visible on the next GET.
    set((s) => {
      const next = new Map(s.recentlyResolvedIds)
      next.delete(itemId)
      return { recentlyResolvedIds: next }
    })
    // 4065a §4.2 synchronous-resolve failure semantics: when the agent
    // (liveness authority) is unreachable the backend returns 502 with error
    // code `agent_unreachable` and does NOT transition the item — it stays
    // `pending`. The optimistic remove has just been rolled back above, so the
    // item is visibly back in the queue. Rethrow with a specific, actionable
    // message (the caller's catch surfaces it as an error toast) rather than
    // the raw "HTTP 502" text, so the operator understands the resolve did not
    // take effect and the item can be retried.
    if (isAgentUnreachableError(e)) {
      throw new Error(AGENT_UNREACHABLE_MESSAGE)
    }
    throw e
  }

  // Fire-and-forget refetch for eventual consistency with the backend
  // (resolved-elsewhere items, queue ordering, etc.). NOT awaited — the
  // optimistic update is already correct from the user's perspective
  // and awaiting it is what caused the ~10s "Rejecting..." hang.
  void get().refetch(reviewerId)

  // Signal any independent polling hooks (e.g. useAwaitingApprovalRuns)
  // that a resolution just happened so they can trigger an immediate
  // refetch rather than waiting for their next polling tick. This is
  // what makes the "Requires Approval" runs tab transition instantly.
  try {
    useResolutionSignal.getState().increment()
  } catch {
    // Guard against test environments where the store may not be wired.
  }

  // Optimistically prepend the just-resolved item to the history list
  // so the operator sees their action reflected immediately without
  // waiting for a refetch. Only when history has been loaded at least
  // once (historyItems.length > 0) to avoid creating a misleading
  // one-item history from an otherwise empty state.
  if (!alreadyResolved) {
    const { historyItems } = get()
    if (historyItems.length > 0) {
      const resolvedNow = new Date().toISOString()
      // Map verb form to past-tense to match what the backend returns in history.
      const resolutionPastTense =
        resolution === 'approve' ? 'approved'
        : resolution === 'reject' ? 'rejected'
        : resolution === 'suppress' ? 'suppressed'
        : resolution
      const optimisticHistoryItem: AttentionItem = {
        ...item,
        resolution: resolutionPastTense,
        resolved_by: reviewerId,
        resolved_at: resolvedNow,
      }
      set((s) => ({
        historyItems: [optimisticHistoryItem, ...s.historyItems],
      }))
    }
  }

  // Resolving an agent tool approval almost always produces new turns
  // server-side (the agent ingests the feedback / proceeds with the
  // call and may emit a new approval request). The sessions transcript
  // cache short-circuits once status is `'complete'`, so without this
  // invalidation the AgentSessionModal would keep showing the stale
  // transcript that ends at the tool call we just resolved. We
  // invalidate by sessionId so the next open — or the live modal —
  // refetches from /history.
  if (item.source === 'agent' && item.session_id) {
    try {
      // Reload only if the modal is currently looking at this session;
      // otherwise just drop the cache and let the next open refetch.
      const sessionsStore = useSessionsStore.getState()
      const isOpen = get().agentSessionModalSessionId === item.session_id
      sessionsStore.invalidateTranscript(item.session_id, isOpen)
    } catch {
      // Sessions store may not be initialised in all test envs.
    }
  }

  return { alreadyResolved }
}

/**
 * Stringify approve-with-edits payload as a feedback note. The unified
 * resolve endpoint has no structured `edits` field, so we serialise the
 * edits + rationale into the audit-log feedback string.
 */
function stringifyEditsAsFeedback(
  edits: Record<string, unknown>,
  rationale?: string,
): string {
  const editsJson = (() => {
    try {
      return JSON.stringify(edits)
    } catch {
      return String(edits)
    }
  })()
  const lines = [`[approved with edits] ${editsJson}`]
  if (rationale && rationale.trim().length > 0) {
    lines.push(rationale.trim())
  }
  return lines.join(' ')
}

interface QueueState {
  // Attention list state (primary data source)
  attentionItems: AttentionItem[]
  attentionCount: number
  attentionHash: string
  tab: AttentionQueueTab
  isLoading: boolean
  isFetching: boolean
  error: string | null

  // History state (unified attention history)
  historyItems: AttentionItem[]
  historyHasMore: boolean
  historyNextCursor: string | null
  historyFilters: QueueFiltersState

  // Assignment state
  assignments: Record<string, string> // item_id → operator_tag
  operatorTag: OperatorTag | null // current operator's tag

  // Detail state
  expandedItemId: string | null
  detailData: ApprovalDetailResponse | null
  detailLoading: boolean
  detailError: string | null

  // Customer history modal state — customer email/id shown in the
  // CustomerHistoryModal popout, or null when the modal is closed. The modal
  // is mounted at the AppShell level and reads this directly from the store.
  customerHistoryModalEmail: string | null
  customerHistoryModalId: string | null
  /** Display name for the window title bar. Null falls back to the email. */
  customerHistoryModalName: string | null
  /**
   * Monotonic counter incremented on every `openCustomerHistoryModal` call.
   *
   * The browser portal renders the history as an in-page panel (Task 4094)
   * rather than an OS window, so "re-open the same customer and see fresh
   * data" has to be a **remount**, not a new window. The panel keys itself on
   * this counter, so opening the same customer twice produces a different key
   * and React tears the subtree down and rebuilds it. Without it, re-opening
   * the identical customer would reuse the mounted component and its already
   * loaded data.
   *
   * Unused on desktop, where `openPopout` guarantees freshness by closing and
   * recreating the `WebviewWindow`.
   */
  customerHistoryModalOpenCount: number

  // Review thread modal state — interaction id of the review whose thread is
  // shown in the ReviewThreadModal popout, or null when the modal is closed.
  // The modal is mounted at the AppShell level (sibling of CustomerHistoryModal)
  // and reads this directly from the store.
  reviewThreadModalInteractionId: string | null
  /** Reviewer display name for the window title bar. Null falls back to a generic title. */
  reviewThreadModalReviewerName: string | null
  /** App name shown as the window subtitle. */
  reviewThreadModalAppName: string | null
  /**
   * Monotonic counter incremented on every `openReviewThreadModal` call.
   * Same remount contract as {@link QueueState.customerHistoryModalOpenCount}.
   */
  reviewThreadModalOpenCount: number

  // Agent session modal state — session_id of the session shown in the
  // AgentSessionModal popout, or null when the modal is closed. The modal is
  // mounted at the QueueView level and reads this directly from the store.
  agentSessionModalSessionId: string | null
  /**
   * Cached agent id for the open modal session. Used by the live-stream
   * subscription which needs both `agent_id` and `session_id` to build the
   * per-session SSE URL. We snapshot this when the modal is opened
   * (typically from an AttentionItem) so the hook can subscribe even
   * after the AttentionItem has been resolved out of `attentionItems`.
   * Falls back to looking up the AttentionItem / sessions store when null.
   */
  agentSessionModalAgentId: string | null
  /**
   * Cached user id for the open modal session.  ADK session storage is keyed
   * on `(app_name, user_id, session_id)`, so passing user_id as a query param
   * to /history lets the backend skip its 7-candidate user_id probing loop
   * (the dominant source of the ~12s "View session" stall on the Runs tab).
   *
   * The Runs tab snapshots this from the agent step's output payload (where
   * the operations backend stashes the user_id alongside session_id —
   * Tier 1 of the View-Session perf fix).  Null when unknown; the modal
   * falls back to letting /history resolve user_id via the SQL filter on
   * StorageSession.id (Tier 2 fast path) and ultimately the candidate loop.
   */
  agentSessionModalUserId: string | null

  // UI state
  keyboardSelectedId: string | null
  newItemIds: Set<string>
  fadingOutIds: Set<string>
  /**
   * Map of item IDs resolved by this operator to the epoch-ms timestamp at
   * which the resolve was issued.  Entries expire after RECENTLY_RESOLVED_TTL_MS.
   *
   * Used by fetchAttentionItems to filter out items the server may still be
   * returning during its eventual-consistency window after a successful
   * resolve POST.  The map value (resolvedAt) is compared against
   * `item.created_at` so that a chained re-pending of the same id — the
   * same execution re-entering the queue with a newer created_at — is NOT
   * suppressed: only ghost items whose created_at pre-dates the resolve are
   * filtered out.
   */
  recentlyResolvedIds: Map<string, number>
  sectionCollapseState: Record<string, boolean>
  selectedIds: Set<string>
  pendingResolutions: Set<string>
  liveEnabled: boolean
  sseState: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error'
  sseLastConnected: Date | null

  // Degraded state (from legacy approval endpoint)
  degraded: boolean
  degradedSources: string[]

  // Polling
  pollingIntervalId: number | null
  // Foreground (5s) polling — runs only while the Attention Queue view is
  // mounted. Kept in a SEPARATE field from pollingIntervalId so the
  // app-shell 10-minute loop and the view-scoped 5s loop never clobber
  // each other's interval id.
  foregroundPollingIntervalId: number | null
}

/**
 * How long (ms) to keep a resolved id in recentlyResolvedIds.
 *
 * Must comfortably exceed the longest observed in-flight POST latency.
 * The Python agent service can block for ~36 seconds on tool-approval
 * items; we use 120 s to cover that plus the reconcileOrphans / Firestore
 * projection consistency window observed in the 2026-05-13 incident.
 */
const RECENTLY_RESOLVED_TTL_MS = 120_000

/**
 * Register one or more item IDs as recently resolved in the store so that
 * the next fetchAttentionItems call cannot restore them from a stale server
 * snapshot.  Each entry expires after RECENTLY_RESOLVED_TTL_MS.
 *
 * The map value is the epoch-ms timestamp at which the resolve was issued.
 * fetchAttentionItems compares this against `item.created_at` so that a
 * chained re-pending of the same id (same execution, new created_at that
 * post-dates the resolve) is NOT suppressed — only stale ghost items are.
 *
 * Extracted into a helper so performOptimisticResolve, bulkApprove, and
 * bulkReject all use the same path — avoiding the gap where bulk actions
 * bypassed the guard entirely.
 */
function markRecentlyResolved(
  set: (
    partial:
      | Partial<QueueState & QueueActions>
      | ((state: QueueState & QueueActions) => Partial<QueueState & QueueActions>),
  ) => void,
  ids: string[],
): void {
  if (ids.length === 0) return
  const resolvedAt = Date.now()
  set((s) => {
    const next = new Map(s.recentlyResolvedIds)
    for (const id of ids) next.set(id, resolvedAt)
    return { recentlyResolvedIds: next }
  })
  window.setTimeout(() => {
    set((s) => {
      const next = new Map(s.recentlyResolvedIds)
      for (const id of ids) {
        // Only prune if the stored timestamp is still ours — a subsequent
        // resolve of the same id (operator retry, or a chained re-pending
        // that the operator also actioned) will have written a newer
        // timestamp and must not be pruned by this earlier timer.
        if (next.get(id) === resolvedAt) next.delete(id)
      }
      return { recentlyResolvedIds: next }
    })
  }, RECENTLY_RESOLVED_TTL_MS)
}

interface QueueActions {
  // Tab actions
  setTab: (tab: AttentionQueueTab) => void
  setOperatorTag: (tag: OperatorTag | null) => void
  setHistoryFilters: (filters: Partial<QueueFiltersState>) => void
  clearHistoryFilters: () => void

  // Data fetching
  fetchAttentionItems: () => Promise<void>
  fetchAttentionCount: () => Promise<void>
  fetchHistory: (operatorId?: string) => Promise<void>
  /** Appends the next page of history items using the stored nextCursor. */
  loadMoreHistory: () => Promise<void>
  fetchAssignments: () => Promise<void>
  refetch: (operatorId?: string) => Promise<void>

  // Detail actions
  setExpandedItem: (id: string | null, attentionType?: AttentionType) => void
  fetchDetail: (id: string) => Promise<void>

  // Customer history modal actions
  openCustomerHistoryModal: (params: {
    email?: string | null
    customerId?: string | null
    name?: string | null
  }) => void
  closeCustomerHistoryModal: () => void

  // Review thread modal actions
  openReviewThreadModal: (params: {
    interactionId: string
    reviewerName?: string | null
    appName?: string | null
  }) => void
  closeReviewThreadModal: () => void

  // Agent session modal actions
  openAgentSessionModal: (
    sessionId: string,
    options?: {
      agentId?: string | null
      /**
       * Owning user_id for the session, if the caller already knows it
       * (e.g. extracted from an automation step's output map).  Passed as a
       * query param to /history so the backend can do a direct partition-key
       * lookup against the ADK session table instead of probing 7 candidate
       * user_ids in series.
       */
      userId?: string | null
    },
  ) => void
  closeAgentSessionModal: () => void

  // Mutation actions — all routed through POST /v1/attention/{id}/resolve.
  // The action signatures take an ApprovalItem-shape for backward
  // compatibility with the existing UI; the store looks the full
  // AttentionItem up by id when calling the resolve endpoint.
  //
  // Return value: OptimisticResolveResult so callers can distinguish the
  // Task 3145f "already_resolved" idempotent-success path from the
  // regular success path and render a neutral info toast for the former.
  approve: (item: Pick<ApprovalItem, 'id' | 'source' | 'source_ref' | 'agent_id'>, reviewerId: string, feedback?: string) => Promise<OptimisticResolveResult>
  suppress: (item: Pick<ApprovalItem, 'id' | 'source' | 'source_ref' | 'agent_id'>, reviewerId: string, reason?: string) => Promise<OptimisticResolveResult>
  reject: (item: Pick<ApprovalItem, 'id' | 'source' | 'source_ref' | 'agent_id'>, reviewerId: string, feedback: RejectFeedback) => Promise<OptimisticResolveResult>
  approveWithEdits: (item: Pick<ApprovalItem, 'id' | 'source' | 'source_ref'>, reviewerId: string, edits: Record<string, unknown>, rationale?: string) => Promise<OptimisticResolveResult>

  // Assignment actions
  assign: (itemId: string, tag: OperatorTag, assignedBy: string) => Promise<void>
  unassign: (itemId: string) => Promise<void>

  // Unified resolution action (routes based on attention_type)
  resolveItem: (item: AttentionItem, resolution: string, reviewerId: string, feedback?: string) => Promise<void>

  // UI actions
  setKeyboardSelectedId: (id: string | null) => void
  setSectionCollapsed: (itemId: string, sectionId: string, collapsed: boolean) => void
  markItemNew: (id: string) => void
  markItemFadingOut: (id: string) => void
  clearAnimations: () => void
  toggleItemSelection: (id: string) => void
  clearSelection: () => void
  toggleLiveMode: () => void
  setSSEState: (state: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error') => void

  // Bulk actions
  bulkApprove: (reviewerId: string) => Promise<{ succeeded: string[]; failed: string[] }>
  bulkReject: (reviewerId: string, feedback: string) => Promise<{ succeeded: string[]; failed: string[] }>

  // Optimistic resolution
  resolveOptimistic: (id: string) => void
  resolveSuccess: (id: string) => void
  resolveFailure: (id: string) => void

  // Polling
  startPolling: (operatorId?: string) => void
  stopPolling: () => void
  // Foreground 5s poll, scoped to the mounted Attention Queue view.
  startForegroundPolling: () => void
  stopForegroundPolling: () => void
}

export const useQueueStore = create<QueueState & QueueActions>((set, get) => ({
  // Initial state
  attentionItems: [],
  attentionCount: 0,
  attentionHash: '',
  tab: 'all',
  isLoading: false,
  isFetching: false,
  error: null,

  historyItems: [],
  historyFilters: {},
  historyHasMore: false,
  historyNextCursor: null,

  assignments: {},
  operatorTag: loadOperatorTag(),

  expandedItemId: null,
  detailData: null,
  detailLoading: false,
  detailError: null,

  customerHistoryModalEmail: null,
  customerHistoryModalId: null,
  customerHistoryModalName: null,
  customerHistoryModalOpenCount: 0,
  reviewThreadModalInteractionId: null,
  reviewThreadModalReviewerName: null,
  reviewThreadModalAppName: null,
  reviewThreadModalOpenCount: 0,

  agentSessionModalSessionId: null,
  agentSessionModalAgentId: null,
  agentSessionModalUserId: null,

  keyboardSelectedId: null,
  newItemIds: new Set(),
  fadingOutIds: new Set(),
  recentlyResolvedIds: new Map(),
  sectionCollapseState: {},
  selectedIds: new Set(),
  pendingResolutions: new Set(),
  liveEnabled: true,
  sseState: 'disconnected',
  sseLastConnected: null,

  degraded: false,
  degradedSources: [],

  pollingIntervalId: null,
  foregroundPollingIntervalId: null,

  // Tab actions
  setTab: (tab) => {
    const prevTab = get().tab
    if (prevTab === tab) return

    if (tab === 'history') {
      // History tab needs a fresh fetch from the approval endpoint
      set({ tab, historyItems: [], historyNextCursor: null, isLoading: true, error: null })
    } else {
      set({ tab })
    }
  },

  setOperatorTag: (tag) => {
    saveOperatorTag(tag)
    set({ operatorTag: tag })
  },

  setHistoryFilters: (filters) => {
    set((state) => ({
      historyFilters: { ...state.historyFilters, ...filters },
    }))
  },

  clearHistoryFilters: () => {
    set({ historyFilters: {} })
  },

  // Data fetching — attention items (primary source)
  fetchAttentionItems: async () => {
    const { isLoading, attentionItems } = get()

    // Set loading only on initial fetch
    if (!isLoading && attentionItems.length === 0) {
      set({ isLoading: true })
    }
    set({ isFetching: true, error: null })

    try {
      const response = await getAttentionItems()
      const prevItems = get().attentionItems
      const prevItemIds = new Set(prevItems.map((i) => i.id))
      const currentIds = new Set(response.items.map((i) => i.id))

      // Detect new items for animation
      const newIds = new Set<string>()
      for (const item of response.items) {
        if (!prevItemIds.has(item.id)) {
          newIds.add(item.id)
        }
      }

      // Detect removed items for fade out
      const fadingIds = new Set<string>()
      for (const item of prevItems) {
        if (!currentIds.has(item.id)) {
          fadingIds.add(item.id)
        }
      }

      // Seed the assignments map from claimed_by on each item (Task 3201a).
      // Items remain visible to all operators regardless of ownership, but
      // the chip/filters need the per-id map. We don't blow away existing
      // entries because a separate /v1/attention/assignments fetch may have
      // surfaced stale agent claims that aren't in the current list.
      const seededAssignments: Record<string, string> = { ...get().assignments }
      for (const item of response.items) {
        if (item.claimed_by) {
          seededAssignments[item.id] = item.claimed_by
        } else {
          delete seededAssignments[item.id]
        }
      }

      // Filter out items that were resolved by this operator very recently.
      // The attention endpoint has a brief eventual-consistency window: a
      // successful resolve POST does not guarantee the item is absent from
      // the very next GET /v1/attention snapshot.  Any GET that lands while
      // a resolve POST is in-flight (fire-and-forget refetches, route
      // remounts, the refresh button) can therefore return a stale row and
      // re-add the just-resolved item to the list.
      //
      // recentlyResolvedIds is a Map<id, resolvedAt (epoch ms)>.  We
      // compare resolvedAt against item.created_at so that a chained
      // re-pending of the same id — the same execution re-entering the
      // queue with a newer created_at that post-dates the resolve — is NOT
      // suppressed.  Only ghost copies (created_at ≤ resolvedAt) are
      // filtered out.
      const recentlyResolved = get().recentlyResolvedIds
      const freshItems = recentlyResolved.size > 0
        ? response.items.filter((item) => {
            const resolvedAt = recentlyResolved.get(item.id)
            if (resolvedAt === undefined) return true
            // A chained re-pending has a created_at that post-dates the
            // resolve — show it immediately rather than suppressing it.
            const itemCreatedAt = item.created_at
              ? new Date(item.created_at).getTime()
              : 0
            return itemCreatedAt > resolvedAt
          })
        : response.items

      // attentionCount is derived from freshItems.length rather than
      // response.count so that the sidebar badge reflects the filtered
      // list (i.e. excludes recently-resolved items that the server is
      // still returning in its snapshot).  This is correct as long as
      // GET /v1/attention returns the full unfiltered list in a single
      // response (no server-side pagination).  If pagination is ever
      // added to that endpoint, attentionCount will need to come from
      // response.count adjusted by the number of filtered-out items
      // rather than from freshItems.length directly.
      set({
        attentionItems: freshItems,
        attentionCount: freshItems.length,
        assignments: seededAssignments,
        isLoading: false,
        isFetching: false,
        newItemIds: newIds.size > 0 ? newIds : get().newItemIds,
        fadingOutIds: fadingIds.size > 0 ? fadingIds : get().fadingOutIds,
      })

      // Clear animations after delay
      if (newIds.size > 0 || fadingIds.size > 0) {
        setTimeout(() => {
          set({ newItemIds: new Set(), fadingOutIds: new Set() })
        }, 1000)
      }
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : String(e),
        isLoading: false,
        isFetching: false,
      })
    }
  },

  fetchAttentionCount: async () => {
    try {
      const response = await getAttentionCount()
      const currentHash = get().attentionHash

      // Only refetch full list if hash changed
      if (response.hash !== currentHash) {
        set({ attentionHash: response.hash, attentionCount: response.count })
        await get().fetchAttentionItems()
      }
    } catch (e) {
      console.error('Failed to fetch attention count:', e)
    }
  },

  // History tab — uses the unified attention history endpoint.
  // Always resets to page 1 (no cursor) so filters and tab changes get
  // fresh data from the most-recently-resolved items.
  fetchHistory: async () => {
    const { historyFilters } = get()

    set({ isFetching: true, error: null })

    try {
      const response = await getAttentionHistory({
        limit: 50,
        resolution: historyFilters.outcome && historyFilters.outcome !== 'all'
          ? historyFilters.outcome
          : undefined,
        // No cursor — always start from the first (most recent) page.
      })
      set({
        historyItems: response.items,
        historyHasMore: response.has_more,
        historyNextCursor: response.next_cursor ?? null,
        isLoading: false,
        isFetching: false,
      })
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : String(e),
        isLoading: false,
        isFetching: false,
      })
    }
  },

  // Appends the next page of history items to the existing list using the
  // cursor from the last fetchHistory response.
  loadMoreHistory: async () => {
    const { historyFilters, historyNextCursor, historyItems } = get()
    if (!historyNextCursor) return

    set({ isFetching: true })

    try {
      const response = await getAttentionHistory({
        limit: 50,
        resolution: historyFilters.outcome && historyFilters.outcome !== 'all'
          ? historyFilters.outcome
          : undefined,
        cursor: historyNextCursor,
      })
      set({
        // Deduplicate by ID in case an optimistic prepend created a duplicate
        // for an item that now also appears on the fetched page.
        historyItems: deduplicateHistoryItems([...historyItems, ...response.items]),
        historyHasMore: response.has_more,
        historyNextCursor: response.next_cursor ?? null,
        isFetching: false,
      })
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : String(e),
        isFetching: false,
      })
    }
  },

  fetchAssignments: async () => {
    if (!ASSIGNMENT_ENABLED) {
      return
    }
    try {
      const response = await getAttentionAssignments()
      set({ assignments: response.assignments })
    } catch (e) {
      console.error('Failed to fetch assignments:', e)
    }
  },

  refetch: async (operatorId) => {
    const { tab } = get()
    if (tab === 'history') {
      await get().fetchHistory(operatorId)
    } else {
      await get().fetchAttentionItems()
      await get().fetchAssignments()
    }
  },

  // Detail actions
  setExpandedItem: (id, attentionType) => {
    set({
      expandedItemId: id,
      detailData: null,
      detailError: null,
      keyboardSelectedId: null,
    })
    // Only build approval detail for tool-approval items. Text reviews
    // and PR reviews render directly from the AttentionItem in their
    // dedicated panels.
    if (id && attentionType && isToolApproval(attentionType)) {
      get().fetchDetail(id)
    }
  },

  /**
   * Build the approval detail panel for a tool-approval AttentionItem.
   *
   * The legacy /v1/approvals/{id} endpoint no longer exists in the
   * gateway — the platform unified on /v1/attention, which carries the
   * full payload inline in `AttentionItem.details`. We synthesise the
   * card-compatible response from the in-memory AttentionItem using the
   * type-aware renderer for display.
   */
  fetchDetail: async (id) => {
    set({ detailLoading: true, detailError: null })

    // Find the item from the in-memory list (active or history).
    const { attentionItems, historyItems } = get()
    const item =
      attentionItems.find((i) => i.id === id) ??
      historyItems.find((i) => i.id === id)

    if (!item) {
      set({
        detailError:
          'Item not found in current list. Try refreshing the queue.',
        detailLoading: false,
      })
      return
    }

    try {
      const data = buildSyntheticDetail(item)
      set({ detailData: data, detailLoading: false })
    } catch (e) {
      set({
        detailError: e instanceof Error ? e.message : String(e),
        detailLoading: false,
      })
    }
  },

  // Customer history modal — opens a CustomerHistoryModal popout for the
  // given customer email / id. Mounted at the AppShell level; reads
  // `customerHistoryModalEmail` / `customerHistoryModalId` from the store.
  //
  // Two consumers with different lifetimes (Task 4094): on desktop the modal
  // spawns an OS window and resets this state immediately, so the store holds
  // it only for the duration of one effect. In the browser it drives an
  // in-page panel, so the state must persist until the operator dismisses it.
  // The modal owns that distinction; the actions here are identical either way.
  openCustomerHistoryModal: ({ email, customerId, name }) => {
    set((s) => ({
      customerHistoryModalEmail: email ?? null,
      customerHistoryModalId: customerId ?? null,
      customerHistoryModalName: name ?? null,
      // Bump unconditionally, including when the same customer is re-opened —
      // that is precisely the case the browser panel needs to remount for.
      customerHistoryModalOpenCount: s.customerHistoryModalOpenCount + 1,
    }))
  },

  closeCustomerHistoryModal: () => {
    set({
      customerHistoryModalEmail: null,
      customerHistoryModalId: null,
      customerHistoryModalName: null,
    })
  },

  // Review thread modal — opens a ReviewThreadModal popout for the given
  // review interaction id. Mounted at the AppShell level; reads
  // `reviewThreadModalInteractionId` from the store.
  openReviewThreadModal: ({ interactionId, reviewerName, appName }) => {
    set((s) => ({
      reviewThreadModalInteractionId: interactionId,
      reviewThreadModalReviewerName: reviewerName ?? null,
      reviewThreadModalAppName: appName ?? null,
      reviewThreadModalOpenCount: s.reviewThreadModalOpenCount + 1,
    }))
  },

  closeReviewThreadModal: () => {
    set({
      reviewThreadModalInteractionId: null,
      reviewThreadModalReviewerName: null,
      reviewThreadModalAppName: null,
    })
  },

  // Agent session modal — opens the AgentSessionModal popout for the given
  // session, or closes it. The modal itself is mounted at the QueueView level
  // and reads `agentSessionModalSessionId` from the store. The transcript
  // data is loaded lazily by the modal body using `useSessionsStore`'s
  // `loadTranscriptChunk` (cached by sessionId), so opening the same session
  // a second time is a no-op network-wise.
  openAgentSessionModal: (sessionId, options) => {
    // If an agentId is provided up-front (e.g. by the toast CTA opened from
    // a just-rejected AttentionItem), snapshot it so the live-stream hook
    // can subscribe even after the source AttentionItem has been removed
    // from `attentionItems` by the optimistic resolve.
    //
    // userId is a separate snapshot used purely as a /history query param
    // hint so the agents backend can skip its candidate-loop probe — see
    // `agentSessionModalUserId` JSDoc on the state shape for context.
    set({
      agentSessionModalSessionId: sessionId,
      agentSessionModalAgentId: options?.agentId ?? null,
      agentSessionModalUserId: options?.userId ?? null,
    })
  },

  closeAgentSessionModal: () => {
    set({
      agentSessionModalSessionId: null,
      agentSessionModalAgentId: null,
      agentSessionModalUserId: null,
    })
  },

  // Mutation actions — all routed through POST /v1/attention/{id}/resolve
  // (the legacy /v1/approvals/* endpoints no longer exist). The action
  // signatures still take an ApprovalItem-shape for backward compatibility
  // with the existing UI; we look the full AttentionItem up by id from
  // the store to populate the resolve payload.
  //
  // All four actions are optimistic: the item is removed from
  // `attentionItems` and `attentionCount` is decremented before the
  // network call. The expanded detail panel is closed synchronously.
  // The POST runs in the foreground (callers `await` it so the action
  // bar / modal can close on success and show an error on failure), but
  // the post-resolve `refetch()` is fired and intentionally NOT awaited
  // — eventual consistency is fine because we already updated the list
  // locally. If the POST fails we restore the item to its original
  // index and rethrow so the caller can surface the error.

  approve: async (item, reviewerId, feedback) => {
    return performOptimisticResolve(get, set, item.id, 'approve', reviewerId, feedback)
  },

  suppress: async (item, reviewerId, reason) => {
    return performOptimisticResolve(get, set, item.id, 'suppress', reviewerId, reason)
  },

  reject: async (item, reviewerId, feedback) => {
    // The unified resolve endpoint accepts a single `feedback` string.
    // Quick-reject reason chips (when present) are serialised in front of
    // the freetext until the backend supports a structured `quick_reasons`
    // field — see comment in serializeRejectFeedback below.
    const serialised = serializeRejectFeedback(feedback)
    return performOptimisticResolve(get, set, item.id, 'reject', reviewerId, serialised)
  },

  approveWithEdits: async (item, reviewerId, edits, rationale) => {
    // The unified resolve endpoint doesn't have a structured `edits`
    // field. We approve and append the edits + rationale into the
    // feedback string so they're at least visible in the audit log.
    const note = stringifyEditsAsFeedback(edits, rationale)
    return performOptimisticResolve(get, set, item.id, 'approve', reviewerId, note)
  },

  // Assignment actions
  //
  // Backend assignment endpoints don't exist yet — see featureFlags.ts.
  // When ASSIGNMENT_ENABLED is false the actions throw a clear error
  // rather than silently making 404 requests. The UI hides the
  // assignment surfaces in that mode so this code path shouldn't fire.

  assign: async (itemId, tag, assignedBy) => {
    if (!ASSIGNMENT_ENABLED) {
      throw new Error(
        'Operator assignment is not yet supported by the backend. Coming soon.',
      )
    }
    // Optimistic update
    set((state) => ({
      assignments: { ...state.assignments, [itemId]: tag },
    }))
    try {
      await assignAttentionItem(itemId, tag, assignedBy)
    } catch (e) {
      // Revert on failure
      set((state) => {
        const next = { ...state.assignments }
        delete next[itemId]
        return { assignments: next }
      })
      throw e
    }
  },

  unassign: async (itemId) => {
    if (!ASSIGNMENT_ENABLED) {
      throw new Error(
        'Operator assignment is not yet supported by the backend. Coming soon.',
      )
    }
    // Optimistic update
    const prev = get().assignments[itemId]
    set((state) => {
      const next = { ...state.assignments }
      delete next[itemId]
      return { assignments: next }
    })
    try {
      await unassignAttentionItem(itemId)
    } catch (e) {
      // Revert on failure
      if (prev) {
        set((state) => ({
          assignments: { ...state.assignments, [itemId]: prev },
        }))
      }
      throw e
    }
  },

  // Unified resolution action
  resolveItem: async (item, resolution, reviewerId, feedback) => {
    await resolveAttentionItem({
      id: item.id,
      source: item.source,
      attentionType: item.attention_type,
      resolution,
      executionId: item.execution_id,
      agentId: item.agent_id,
      sessionId: item.session_id,
      reviewerId,
      feedback: feedback ?? null,
      detailsSnapshot: item.details ?? undefined,
    })
    set({ expandedItemId: null, detailData: null })
    await get().fetchAttentionItems()
  },

  // UI actions
  setKeyboardSelectedId: (id) => {
    set({ keyboardSelectedId: id })
  },

  setSectionCollapsed: (itemId, sectionId, collapsed) => {
    set((state) => ({
      sectionCollapseState: {
        ...state.sectionCollapseState,
        [`${itemId}:${sectionId}`]: collapsed,
      },
    }))
  },

  markItemNew: (id) => {
    set((state) => ({
      newItemIds: new Set([...state.newItemIds, id]),
    }))
  },

  markItemFadingOut: (id) => {
    set((state) => ({
      fadingOutIds: new Set([...state.fadingOutIds, id]),
    }))
  },

  clearAnimations: () => {
    set({ newItemIds: new Set(), fadingOutIds: new Set() })
  },

  toggleItemSelection: (id) => {
    set((state) => {
      const next = new Set(state.selectedIds)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return { selectedIds: next }
    })
  },

  clearSelection: () => {
    set({ selectedIds: new Set() })
  },

  toggleLiveMode: () => {
    set((state) => ({ liveEnabled: !state.liveEnabled }))
  },

  setSSEState: (sseState) => {
    set((state) => ({
      sseState,
      sseLastConnected: sseState === 'connected' ? new Date() : state.sseLastConnected,
    }))
  },

  // Bulk actions
  //
  // Like the single-item resolve actions, these are optimistic: every
  // selected item is removed from `attentionItems` up front and
  // restored on failure. The post-bulk refetch is fire-and-forget for
  // the same reason — eventual consistency is fine and awaiting it
  // produces a multi-second hang on the bulk action bar.
  bulkApprove: async (reviewerId) => {
    const { selectedIds, attentionItems } = get()
    const ids = Array.from(selectedIds)
    const itemsById = new Map(
      attentionItems.filter((i) => selectedIds.has(i.id)).map((i) => [i.id, i]),
    )
    const succeeded: string[] = []
    const failed: string[] = []

    // Optimistic remove for every selected item that we actually have.
    const removedIds = new Set(itemsById.keys())
    set((s) => ({
      attentionItems: s.attentionItems.filter((i) => !removedIds.has(i.id)),
      attentionCount: Math.max(0, s.attentionCount - removedIds.size),
      selectedIds: new Set(),
      expandedItemId:
        s.expandedItemId && removedIds.has(s.expandedItemId)
          ? null
          : s.expandedItemId,
      detailData:
        s.expandedItemId && removedIds.has(s.expandedItemId)
          ? null
          : s.detailData,
    }))

    // Register all selected ids BEFORE the POST loop so the guard is active
    // for every GET that lands while individual POSTs are in-flight.
    // Failed ids are un-registered below so the operator can see and retry.
    markRecentlyResolved(set, ids.filter((id) => itemsById.has(id)))

    for (const id of ids) {
      const item = itemsById.get(id)
      if (!item) continue
      try {
        await resolveAttentionItem({
          id: item.id,
          source: item.source,
          attentionType: item.attention_type,
          // Resolve endpoint expects verb form, not past tense.
          resolution: 'approve',
          executionId: item.execution_id,
          agentId: item.agent_id,
          sessionId: item.session_id,
          reviewerId,
          feedback: null,
          detailsSnapshot: item.details ?? undefined,
        })
        succeeded.push(id)
      } catch {
        failed.push(id)
      }
    }

    // Restore any items whose POST failed so the operator can retry.
    if (failed.length > 0) {
      set((s) => {
        const present = new Set(s.attentionItems.map((i) => i.id))
        const toRestore = failed
          .map((id) => itemsById.get(id))
          .filter((it): it is AttentionItem => !!it && !present.has(it.id))
        // Un-register failed ids so the next GET can show them as still
        // pending — only succeeded ids remain guarded.
        const nextResolved = new Map(s.recentlyResolvedIds)
        for (const id of failed) nextResolved.delete(id)
        if (toRestore.length === 0) return { recentlyResolvedIds: nextResolved }
        return {
          attentionItems: [...toRestore, ...s.attentionItems],
          attentionCount: s.attentionCount + toRestore.length,
          recentlyResolvedIds: nextResolved,
        }
      })
    }

    void get().fetchAttentionItems()
    return { succeeded, failed }
  },

  bulkReject: async (reviewerId, feedback) => {
    const { selectedIds, attentionItems } = get()
    const ids = Array.from(selectedIds)
    const itemsById = new Map(
      attentionItems.filter((i) => selectedIds.has(i.id)).map((i) => [i.id, i]),
    )
    const succeeded: string[] = []
    const failed: string[] = []

    const removedIds = new Set(itemsById.keys())
    set((s) => ({
      attentionItems: s.attentionItems.filter((i) => !removedIds.has(i.id)),
      attentionCount: Math.max(0, s.attentionCount - removedIds.size),
      selectedIds: new Set(),
      expandedItemId:
        s.expandedItemId && removedIds.has(s.expandedItemId)
          ? null
          : s.expandedItemId,
      detailData:
        s.expandedItemId && removedIds.has(s.expandedItemId)
          ? null
          : s.detailData,
    }))

    // Register all selected ids BEFORE the POST loop — same reasoning as
    // bulkApprove: guards any GET that lands while individual POSTs are in-flight.
    markRecentlyResolved(set, ids.filter((id) => itemsById.has(id)))

    for (const id of ids) {
      const item = itemsById.get(id)
      if (!item) continue
      try {
        await resolveAttentionItem({
          id: item.id,
          source: item.source,
          attentionType: item.attention_type,
          // Resolve endpoint expects verb form, not past tense.
          resolution: 'reject',
          executionId: item.execution_id,
          agentId: item.agent_id,
          sessionId: item.session_id,
          reviewerId,
          feedback,
          detailsSnapshot: item.details ?? undefined,
        })
        succeeded.push(id)
      } catch {
        failed.push(id)
      }
    }

    if (failed.length > 0) {
      set((s) => {
        const present = new Set(s.attentionItems.map((i) => i.id))
        const toRestore = failed
          .map((id) => itemsById.get(id))
          .filter((it): it is AttentionItem => !!it && !present.has(it.id))
        // Un-register failed ids so the next GET can show them as pending.
        const nextResolved = new Map(s.recentlyResolvedIds)
        for (const id of failed) nextResolved.delete(id)
        if (toRestore.length === 0) return { recentlyResolvedIds: nextResolved }
        return {
          attentionItems: [...toRestore, ...s.attentionItems],
          attentionCount: s.attentionCount + toRestore.length,
          recentlyResolvedIds: nextResolved,
        }
      })
    }

    // Invalidate transcript caches for every agent session we rejected
    // — see comment in performOptimisticResolve.
    try {
      const sessionsStore = useSessionsStore.getState()
      const openSessionId = get().agentSessionModalSessionId
      for (const id of succeeded) {
        const item = itemsById.get(id)
        if (item?.source === 'agent' && item.session_id) {
          sessionsStore.invalidateTranscript(
            item.session_id,
            item.session_id === openSessionId,
          )
        }
      }
    } catch {
      // Sessions store may not be initialised in all test envs.
    }

    void get().fetchAttentionItems()
    return { succeeded, failed }
  },

  // Optimistic resolution tracking
  resolveOptimistic: (id) => {
    set((state) => ({
      pendingResolutions: new Set([...state.pendingResolutions, id]),
    }))
  },

  resolveSuccess: (id) => {
    set((state) => {
      const next = new Set(state.pendingResolutions)
      next.delete(id)
      return { pendingResolutions: next }
    })
  },

  resolveFailure: (id) => {
    set((state) => {
      const next = new Set(state.pendingResolutions)
      next.delete(id)
      return { pendingResolutions: next }
    })
  },

  // Polling — uses hash-based change detection
  startPolling: (_operatorId) => {
    const { pollingIntervalId } = get()
    if (pollingIntervalId !== null) {
      return // Already polling
    }

    // Initial fetch
    get().fetchAttentionItems()
    get().fetchAssignments()

    // Start polling — use count/hash for change detection
    const intervalId = window.setInterval(() => {
      get().fetchAttentionCount()
    }, POLL_INTERVAL_MS)

    set({ pollingIntervalId: intervalId })
  },

  stopPolling: () => {
    const { pollingIntervalId } = get()
    if (pollingIntervalId !== null) {
      window.clearInterval(pollingIntervalId)
      set({ pollingIntervalId: null })
    }
  },

  // Foreground polling — a fast (5s) loop scoped to the mounted Attention
  // Queue view. Distinct from startPolling/stopPolling (which own the
  // app-wide 10-minute sidebar-badge loop) and stored in a separate
  // interval-id field so the two never clobber each other. Uses the cheap
  // hash/count check so it only refetches the full list when it changes.
  startForegroundPolling: () => {
    const { foregroundPollingIntervalId } = get()
    if (foregroundPollingIntervalId !== null) {
      return // Already polling
    }

    const intervalId = window.setInterval(() => {
      get().fetchAttentionCount()
    }, FOREGROUND_POLL_INTERVAL_MS)

    set({ foregroundPollingIntervalId: intervalId })
  },

  stopForegroundPolling: () => {
    const { foregroundPollingIntervalId } = get()
    if (foregroundPollingIntervalId !== null) {
      window.clearInterval(foregroundPollingIntervalId)
      set({ foregroundPollingIntervalId: null })
    }
  },
}))

/**
 * Extract unique agent names from attention items for the filter dropdown.
 */
export function extractUniqueAgentNames(items: AttentionItem[]): string[] {
  const agents = new Set<string>()
  for (const item of items) {
    if (item.agent_name) {
      agents.add(item.agent_name)
    }
  }
  return Array.from(agents).sort()
}

/**
 * Extract unique tool names from queue items for the filter dropdown.
 */
export function extractUniqueTools(items: { tool_name: string }[]): string[] {
  const tools = new Set<string>()
  for (const item of items) {
    if (item.tool_name) {
      tools.add(item.tool_name)
    }
  }
  return Array.from(tools).sort()
}

/**
 * Extract unique agent IDs from queue items for the filter dropdown.
 */
export function extractUniqueAgents(items: { agent_id: string }[]): string[] {
  const agents = new Set<string>()
  for (const item of items) {
    if (item.agent_id) {
      agents.add(item.agent_id)
    }
  }
  return Array.from(agents).sort()
}
